const IDENTIFY_URL = '/apps/identify';
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const BYTES_IN_MEGABYTE = 1024 * 1024;

class ImageScaner extends HTMLElement {
  constructor() {
    super();

    this.tabButtons = this.querySelectorAll('[data-image-scaner-tab]');
    this.panelElements = this.querySelectorAll('[data-image-scaner-panel]');
    this.dropzoneElement = this.querySelector('[data-image-scaner-dropzone]');
    this.fileInputElement = this.querySelector('[data-image-scaner-file]');
    this.previewElement = this.querySelector('[data-image-scaner-preview]');
    this.previewMediaElement = this.querySelector('[data-image-scaner-preview-media]');
    this.clearButtonElement = this.querySelector('[data-image-scaner-clear]');
    this.videoElement = this.querySelector('[data-image-scaner-video]');
    this.photoElement = this.querySelector('[data-image-scaner-photo]');
    this.placeholderElement = this.querySelector('[data-image-scaner-placeholder]');
    this.scanlineElement = this.querySelector('[data-image-scaner-scanline]');
    this.loaderElement = this.querySelector('[data-image-scaner-loader]');
    this.startButtonElement = this.querySelector('[data-image-scaner-start]');
    this.captureButtonElement = this.querySelector('[data-image-scaner-capture]');
    this.retakeButtonElement = this.querySelector('[data-image-scaner-retake]');
    this.submitButtonElement = this.querySelector('[data-image-scaner-submit]');
    this.messageElement = this.querySelector('[data-image-scaner-message]');
    this.resultElement = this.querySelector('[data-image-scaner-result]');
    this.resultMediaElement = this.querySelector('[data-image-scaner-result-media]');
    this.resultDetectedElement = this.querySelector('[data-image-scaner-result-detected]');
    this.resultTitleElement = this.querySelector('[data-image-scaner-result-title]');
    this.resultScoreElement = this.querySelector('[data-image-scaner-result-score]');
    this.resultLinkElement = this.querySelector('[data-image-scaner-result-link]');
    this.profileElement = this.querySelector('[data-image-scaner-profile]');
    this.profileNoteElement = this.querySelector('[data-image-scaner-profile-note]');
    this.clubNameElement = this.querySelector('[data-image-scaner-club-name]');
    this.clubHistoryElement = this.querySelector('[data-image-scaner-club-history]');
    this.honorsListElement = this.querySelector('[data-image-scaner-honors]');
    this.legendsListElement = this.querySelector('[data-image-scaner-legends]');
    this.shirtDetailsElement = this.querySelector('[data-image-scaner-shirt-details]');
    this.honorTemplateElement = this.querySelector('[data-image-scaner-honor-template]');
    this.legendTemplateElement = this.querySelector('[data-image-scaner-legend-template]');

    this.mediaStream = null;
    this.canvasElement = document.createElement('canvas');
    this.pendingImage = '';
    this.activeMode = 'upload';

    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.clearSelectedImage = this.clearSelectedImage.bind(this);
    this.startCamera = this.startCamera.bind(this);
    this.capturePhoto = this.capturePhoto.bind(this);
    this.retakePhoto = this.retakePhoto.bind(this);
    this.submitImage = this.submitImage.bind(this);
  }

  connectedCallback() {
    this.tabButtons.forEach((tabButtonElement) => tabButtonElement.addEventListener('click', this.handleTabClick));
    this.fileInputElement.addEventListener('change', this.handleFileChange);
    this.dropzoneElement.addEventListener('dragover', this.handleDragOver);
    this.dropzoneElement.addEventListener('dragleave', this.handleDragLeave);
    this.dropzoneElement.addEventListener('drop', this.handleDrop);
    this.clearButtonElement.addEventListener('click', this.clearSelectedImage);
    this.startButtonElement.addEventListener('click', this.startCamera);
    this.captureButtonElement.addEventListener('click', this.capturePhoto);
    this.retakeButtonElement.addEventListener('click', this.retakePhoto);
    this.submitButtonElement.addEventListener('click', this.submitImage);
  }

  disconnectedCallback() {
    this.stopCamera();
  }

  get cameraFacing() {
    return this.dataset.cameraFacing;
  }

  get imageQuality() {
    return Number(this.dataset.imageQuality);
  }

  get maxFileSize() {
    return Number(this.dataset.maxFileSize) * BYTES_IN_MEGABYTE;
  }

  handleTabClick(clickEvent) {
    this.switchMode(clickEvent.currentTarget.dataset.imageScanerTab);
  }

  switchMode(modeName) {
    if (this.activeMode === modeName) return;

    this.activeMode = modeName;
    this.hideMessage();
    this.hideResult();

    this.tabButtons.forEach((tabButtonElement) => {
      const is_active = tabButtonElement.dataset.imageScanerTab === modeName;

      tabButtonElement.classList.toggle('image-scaner__tab--active', is_active);
      tabButtonElement.setAttribute('aria-selected', is_active);
    });

    this.panelElements.forEach((panelElement) => {
      panelElement.hidden = panelElement.dataset.imageScanerPanel !== modeName;
    });

    if (modeName === 'upload') {
      this.stopCamera();
      this.resetCameraView();
    } else {
      this.clearSelectedImage();
    }
  }

  handleDragOver(dragEvent) {
    dragEvent.preventDefault();
    this.dropzoneElement.classList.add('image-scaner__dropzone--dragover');
  }

  handleDragLeave() {
    this.dropzoneElement.classList.remove('image-scaner__dropzone--dragover');
  }

  handleDrop(dropEvent) {
    dropEvent.preventDefault();
    this.dropzoneElement.classList.remove('image-scaner__dropzone--dragover');

    const droppedFiles = dropEvent.dataTransfer.files;

    if (droppedFiles.length) this.selectImageFile(droppedFiles[0]);
  }

  handleFileChange(changeEvent) {
    const selectedFiles = changeEvent.target.files;

    if (selectedFiles.length) this.selectImageFile(selectedFiles[0]);
  }

  async selectImageFile(imageFile) {
    this.hideMessage();
    this.hideResult();

    if (!ALLOWED_IMAGE_TYPES.includes(imageFile.type)) {
      this.clearSelectedImage();
      this.showMessage(this.dataset.fileErrorText);
      return;
    }

    if (imageFile.size > this.maxFileSize) {
      this.clearSelectedImage();
      this.showMessage(this.dataset.sizeErrorText.replace('[size]', this.dataset.maxFileSize));
      return;
    }

    const base64Image = await this.readFileAsDataUrl(imageFile);

    this.previewMediaElement.src = base64Image;
    this.previewMediaElement.alt = imageFile.name;
    this.previewElement.hidden = false;
    this.dropzoneElement.hidden = true;

    this.setPendingImage(base64Image);
  }

  readFileAsDataUrl(imageFile) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.addEventListener('load', () => resolve(fileReader.result));
      fileReader.addEventListener('error', () => reject(fileReader.error));
      fileReader.readAsDataURL(imageFile);
    });
  }

  clearSelectedImage() {
    this.fileInputElement.value = '';
    this.previewMediaElement.removeAttribute('src');
    this.previewMediaElement.alt = '';
    this.previewElement.hidden = true;
    this.dropzoneElement.hidden = false;

    this.clearPendingImage();
    this.hideResult();
  }

  setPendingImage(base64Image) {
    this.pendingImage = base64Image;
    this.submitButtonElement.disabled = false;
  }

  clearPendingImage() {
    this.pendingImage = '';
    this.submitButtonElement.disabled = true;
  }

  async startCamera() {
    this.hideMessage();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.showMessage(this.dataset.cameraErrorText);
      return;
    }

    this.startButtonElement.setAttribute('disabled', '');

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.cameraFacing },
        audio: false,
      });
    } catch (cameraError) {
      this.startButtonElement.removeAttribute('disabled');
      this.showMessage(`${this.dataset.cameraErrorText} ${cameraError.message}`);
      return;
    }

    this.videoElement.srcObject = this.mediaStream;
    this.videoElement.hidden = false;
    this.videoElement.play().catch(() => {});
    this.placeholderElement.hidden = true;
    this.scanlineElement.hidden = false;
    this.startButtonElement.hidden = true;
    this.startButtonElement.removeAttribute('disabled');
    this.captureButtonElement.hidden = false;
  }

  stopCamera() {
    if (!this.mediaStream) return;

    this.mediaStream.getTracks().forEach((mediaTrack) => mediaTrack.stop());
    this.mediaStream = null;
    this.videoElement.srcObject = null;
  }

  getCapturedImage() {
    this.canvasElement.width = this.videoElement.videoWidth;
    this.canvasElement.height = this.videoElement.videoHeight;

    const canvasContext = this.canvasElement.getContext('2d');
    canvasContext.drawImage(this.videoElement, 0, 0, this.canvasElement.width, this.canvasElement.height);

    return this.canvasElement.toDataURL('image/jpeg', this.imageQuality);
  }

  capturePhoto() {
    if (!this.videoElement.videoWidth) return;

    const base64Image = this.getCapturedImage();

    this.photoElement.src = base64Image;
    this.photoElement.hidden = false;
    this.videoElement.hidden = true;
    this.scanlineElement.hidden = true;
    this.captureButtonElement.hidden = true;
    this.retakeButtonElement.hidden = false;
    this.stopCamera();

    this.setPendingImage(base64Image);
  }

  retakePhoto() {
    this.hideMessage();
    this.hideResult();
    this.clearPendingImage();

    this.photoElement.hidden = true;
    this.photoElement.removeAttribute('src');
    this.retakeButtonElement.hidden = true;

    this.startCamera();
  }

  resetCameraView() {
    this.clearPendingImage();
    this.photoElement.hidden = true;
    this.photoElement.removeAttribute('src');
    this.videoElement.hidden = true;
    this.scanlineElement.hidden = true;
    this.placeholderElement.hidden = false;
    this.startButtonElement.hidden = false;
    this.captureButtonElement.hidden = true;
    this.retakeButtonElement.hidden = true;
  }

  async submitImage() {
    if (!this.pendingImage) return;

    this.hideMessage();
    this.hideResult();
    this.setLoading(true);

    try {
      const identifyResponse = await fetch(IDENTIFY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          { image: this.pendingImage ,
            lang: window.Shopify.locale
          }
        ),
      });

      if (!identifyResponse.ok) {
        throw new Error(`Identify request failed with status ${identifyResponse.status}`);
      }

      const identifyData = await identifyResponse.json();

      if (!identifyData.ok) {
        throw new Error(identifyData.error || 'Identify request returned an unsuccessful response');
      }

      this.renderResult(identifyData);

      document.dispatchEvent(
        new CustomEvent('image-scaner:identified', {
          detail: { scanerId: this.dataset.scanerId, identifyData },
        })
      );
    } catch (identifyError) {
      this.showMessage(this.dataset.requestErrorText);

      document.dispatchEvent(
        new CustomEvent('image-scaner:failed', {
          detail: { scanerId: this.dataset.scanerId, message: identifyError.message },
        })
      );
    } finally {
      this.setLoading(false);
    }
  }

  renderResult(identifyData) {
    const profileData = this.getProfileData(identifyData);
    const firstMatch = (identifyData.matches || [])[0];

    if (firstMatch) this.renderMatch(identifyData, firstMatch);
    if (profileData) this.renderProfile(profileData, identifyData);

    if (!firstMatch && !profileData) this.showMessage(this.dataset.noMatchText);
  }

  getProfileData(identifyData) {
    const fallbackData = identifyData.fallback || {};
    const profileData = identifyData.profile || fallbackData.profile;

    if (!profileData || !profileData.club || !profileData.club.name) return null;

    return profileData;
  }

  renderMatch(identifyData, firstMatch) {
    if (firstMatch.thumbnailUrl) {
      this.resultMediaElement.src = firstMatch.thumbnailUrl;
      this.resultMediaElement.alt = firstMatch.designLabel || '';
      this.resultMediaElement.hidden = false;
    } else {
      this.resultMediaElement.removeAttribute('src');
      this.resultMediaElement.hidden = true;
    }

    const detectedLabel = identifyData.query ? identifyData.query.designLabel : '';
    this.resultDetectedElement.textContent = detectedLabel || '';
    this.resultDetectedElement.hidden = !detectedLabel;

    this.resultTitleElement.textContent = firstMatch.designLabel || '';

    const scorePercent = Math.round((firstMatch.score || 0) * 100);
    this.resultScoreElement.textContent = this.dataset.scoreText.replace('[score]', scorePercent);

    if (firstMatch.pageUrl) {
      this.resultLinkElement.href = firstMatch.pageUrl;
      this.resultLinkElement.hidden = false;
    } else {
      this.resultLinkElement.removeAttribute('href');
      this.resultLinkElement.hidden = true;
    }

    this.resultElement.hidden = false;
  }

  renderProfile(profileData, identifyData) {
    const clubData = profileData.club || {};
    const shirtData = profileData.shirt || {};
    const fallbackNote = (identifyData.fallback || {}).note;
    const noteText = identifyData.matched ? fallbackNote : fallbackNote || this.dataset.fallbackText;

    this.profileNoteElement.textContent = noteText || '';
    this.profileNoteElement.hidden = !noteText;

    this.clubNameElement.textContent = clubData.name;

    this.setProfileFact('founded', clubData.founded_year);
    this.setProfileFact('stadium', clubData.stadium);
    this.setProfileFact('location', this.getLocationText(shirtData));

    this.clubHistoryElement.textContent = clubData.history || '';
    this.toggleProfileGroup('history', Boolean(clubData.history));

    this.renderHonors(profileData.honors || []);
    this.renderLegends(profileData.legends || []);
    this.renderShirt(shirtData);

    this.profileElement.hidden = false;

    this.profileElement.scrollIntoView({ behavior: 'smooth' });
  }

  getLocationText(shirtData) {
    return [shirtData.city, shirtData.country].filter(Boolean).join(', ');
  }

  setProfileFact(factName, factValue) {
    const factElement = this.querySelector(`[data-image-scaner-fact="${factName}"]`);
    const valueElement = this.querySelector(`[data-image-scaner-fact-value="${factName}"]`);

    valueElement.textContent = factValue || '';
    factElement.hidden = !factValue;
  }

  toggleProfileGroup(groupName, is_visible) {
    this.querySelector(`[data-image-scaner-group="${groupName}"]`).hidden = !is_visible;
  }

  renderHonors(honors) {
    this.honorsListElement.replaceChildren();

    honors.forEach((honorData) => {
      if (!honorData.title) return;

      const honorElement = this.honorTemplateElement.content.cloneNode(true);
      const countElement = honorElement.querySelector('[data-image-scaner-honor-count]');

      countElement.textContent = honorData.count || '';
      countElement.hidden = !honorData.count;
      honorElement.querySelector('[data-image-scaner-honor-title]').textContent = honorData.title;

      this.honorsListElement.append(honorElement);
    });

    this.toggleProfileGroup('honors', this.honorsListElement.children.length > 0);
  }

  renderLegends(legends) {
    this.legendsListElement.replaceChildren();

    legends.forEach((legendData) => {
      if (!legendData.name) return;

      const legendElement = this.legendTemplateElement.content.cloneNode(true);

      legendElement.querySelector('[data-image-scaner-legend-name]').textContent = legendData.name;
      legendElement.querySelector('[data-image-scaner-legend-position]').textContent = legendData.position || '';
      legendElement.querySelector('[data-image-scaner-legend-years]').textContent = legendData.years || '';

      this.legendsListElement.append(legendElement);
    });

    this.toggleProfileGroup('legends', this.legendsListElement.children.length > 0);
  }

  renderShirt(shirtData) {
    const has_season = this.setShirtValue('season', shirtData.season);
    const has_type = this.setShirtValue('type', this.getShirtTypeText(shirtData.type));
    const has_manufacturer = this.setShirtValue('manufacturer', shirtData.manufacturer);
    const has_sponsor = this.setShirtValue('sponsor', shirtData.sponsor);

    this.shirtDetailsElement.textContent = shirtData.details || '';
    this.shirtDetailsElement.hidden = !shirtData.details;

    const has_shirt_data =
      has_season || has_type || has_manufacturer || has_sponsor || Boolean(shirtData.details);

    this.toggleProfileGroup('shirt', has_shirt_data);
  }

  setShirtValue(fieldName, fieldValue) {
    const rowElement = this.querySelector(`[data-image-scaner-shirt-row="${fieldName}"]`);
    const valueElement = this.querySelector(`[data-image-scaner-shirt-value="${fieldName}"]`);

    if (!rowElement) return false;

    valueElement.textContent = fieldValue || '';
    rowElement.hidden = !fieldValue;

    return Boolean(fieldValue);
  }

  getShirtTypeText(typeName) {
    if (!typeName) return '';

    const typeLabels = {
      home: this.dataset.typeHomeText,
      away: this.dataset.typeAwayText,
      third: this.dataset.typeThirdText,
      goalkeeper: this.dataset.typeGoalkeeperText,
    };

    return typeLabels[typeName.toLowerCase()] || typeName;
  }

  hideProfile() {
    this.profileElement.hidden = true;
  }

  hideResult() {
    this.resultElement.hidden = true;
    this.hideProfile();
  }

  setLoading(is_loading) {
    this.loaderElement.hidden = !is_loading;
    this.submitButtonElement.disabled = is_loading || !this.pendingImage;
    this.clearButtonElement.disabled = is_loading;
    this.retakeButtonElement.disabled = is_loading;
    this.tabButtons.forEach((tabButtonElement) => {
      tabButtonElement.disabled = is_loading;
    });
  }

  showMessage(messageText) {
    this.messageElement.textContent = messageText;
    this.messageElement.hidden = false;
  }

  hideMessage() {
    this.messageElement.textContent = '';
    this.messageElement.hidden = true;
  }
}

customElements.define('image-scaner', ImageScaner);
